import React, { useMemo } from 'react';
import { CollapsibleSection, ManifestRowsScroller, ManifestRowsTableHead } from './ManifestRowsTable';
import { buildCellChangeOverlay, type CellChange } from '../../helpers/buildCellChangeOverlay';
import type { PreviewColumn, PreviewRow } from '../../state/api/organisationEngineSlice';

type UpdateRowsTableProps = {
    updateRows: Array<Extract<PreviewRow, { status: 'update' }>>;
    columns: PreviewColumn[];
};

// Mirrors ErrorRowsTable's layout but signals "informational, not a problem"
// via a softer blue accent on the changed cells. Ops gets a preview of
// exactly which fields the engine will write before they hit Commit.
export const UpdateRowsTable = ({ updateRows, columns }: UpdateRowsTableProps) => (
    <CollapsibleSection
        testId='preview-update-rows'
        title={`${updateRows.length} ${updateRows.length === 1 ? 'row' : 'rows'} that will be updated`}
        description='Cells highlighted in blue are the fields the engine will write.'
        defaultOpen
    >
        <ManifestRowsScroller>
            <table className='w-full border-collapse text-sm'>
                <ManifestRowsTableHead columns={columns} />
                <tbody>
                    {updateRows.map(updateRow => (
                        <UpdateRow key={updateRow.row} updateRow={updateRow} columns={columns} />
                    ))}
                </tbody>
            </table>
        </ManifestRowsScroller>
    </CollapsibleSection>
);

const UpdateRow = ({ updateRow, columns }: { updateRow: Extract<PreviewRow, { status: 'update' }>; columns: PreviewColumn[] }) => {
    const overlay = useMemo(() => buildCellChangeOverlay(updateRow.changes, columns), [updateRow.changes, columns]);

    return (
        <tr data-testid={`update-row-${updateRow.row}`} className='border-b border-grey-20 align-top odd:bg-grey-10'>
            <td className='p-2 font-mono text-grey-70'>{updateRow.row}</td>
            {columns.map((col, colIndex) => (
                <ChangeCell key={col.key} value={updateRow.cells[col.key] ?? ''} change={overlay[colIndex]} columnIndex={colIndex} />
            ))}
        </tr>
    );
};

const ChangeCell = ({ value, change, columnIndex }: { value: string; change: CellChange | undefined; columnIndex: number }) => {
    const isChanged = change?.changed ?? false;
    const cellClass = ['p-2', isChanged ? 'border-l-4 border-blue-100 bg-blue-10' : ''].filter(Boolean).join(' ');
    const valueClass = [
        'font-mono text-sm',
        value === '' ? 'italic text-grey-70' : isChanged ? 'text-blue-100 font-semibold' : 'text-grey-100',
    ].join(' ');
    const displayValue = value === '' ? '—' : value;

    return (
        <td
            className={cellClass}
            data-testid={isChanged ? `change-cell-${columnIndex}` : undefined}
            data-cell-changed={isChanged ? 'true' : undefined}
        >
            <div className={valueClass}>{displayValue}</div>
        </td>
    );
};
